import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Radio } from 'lucide-react';

const LINKS = [
  { id: 'worlds', label: 'Worlds' },
  { id: 'united99', label: 'United 99', to: '/u99' },
  { id: 'apps', label: 'Apps' },
  { id: 'books', label: 'Books' },
  { id: 'research', label: 'Research' },
  { id: 'mind', label: "Kyle's Mind" },
  { id: 'signal', label: 'Library' },
];

export default function Navbar({ onNavigate }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    onNavigate(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? 'bg-[#02040a]/80 backdrop-blur-2xl border-b border-cyan-500/10' : 'bg-transparent'
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <button
          onClick={() => go('top')}
          className="flex items-center gap-2.5 group"
          data-testid="nav-brand-logo"
        >
          <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse-dot" />
          <span className="whitespace-nowrap font-celestial text-sm tracking-[0.25em] text-slate-100 group-hover:text-cyan-300 transition-colors duration-300">
            SIDETRACK <span className="text-cyan-400">AI</span> STUDIOS
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
          {LINKS.map((l) => l.to ? (
            <Link
              key={l.id}
              to={l.to}
              onClick={() => setOpen(false)}
              className="whitespace-nowrap font-mono-precision text-[11px] uppercase tracking-[0.2em] text-amber-300/90 hover:text-amber-200 transition-colors duration-300"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </Link>
          ) : (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className="whitespace-nowrap font-mono-precision text-[11px] uppercase tracking-[0.2em] text-slate-400 hover:text-cyan-300 transition-colors duration-300"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4 pl-6">
          <span className="hidden 2xl:flex items-center gap-2 whitespace-nowrap font-mono-precision text-[10px] tracking-[0.2em] text-emerald-400/80" data-testid="nav-status-indicator">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-dot" />
            NODE 01: ACTIVE
          </span>
          <button
            onClick={() => go('signal')}
            className="hidden sm:flex items-center gap-2 whitespace-nowrap font-mono-precision text-[11px] uppercase tracking-[0.2em] text-[#02040a] bg-cyan-400 hover:bg-cyan-300 px-4 py-2 rounded-full transition-all duration-300 hover:shadow-[0_0_20px_rgba(34,211,238,0.45)]"
            data-testid="nav-transmit-signal-btn"
          >
            <Radio size={13} />
            Transmit Signal
          </button>
          <button
            className="lg:hidden text-slate-300"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            className="lg:hidden overflow-hidden bg-[#02040a]/95 backdrop-blur-2xl border-b border-cyan-500/10"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              {LINKS.map((l) => l.to ? (
                <Link key={l.id} to={l.to} onClick={() => setOpen(false)} className="text-left font-mono-precision text-xs uppercase tracking-[0.2em] text-amber-300 hover:text-amber-200" data-testid={`nav-mobile-link-${l.id}`}>
                  {l.label}
                </Link>
              ) : (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="text-left font-mono-precision text-xs uppercase tracking-[0.2em] text-slate-300 hover:text-cyan-300"
                  data-testid={`nav-mobile-link-${l.id}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
